import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { GetEmailTemplates, CreateEmailTemplate, UpdateEmailTemplate, DeleteEmailTemplate, GetTemplateVariables } from './email-templates.actions';
import { MessageService } from 'app/messages/message-service/message.service';
import { EmailTemplate } from 'app/models/dto/email-template.model';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { CreateNewSettingsAlert } from 'app/store/settings/alerts/settings-alerts.actions';
import { TemplateVariable } from 'app/messages/message-service/template-variable.model';

export class EmailTemplateStateModel {
  emailTemplates: EmailTemplate[];
  loading: boolean;
  variables: TemplateVariable[];
}

@State<EmailTemplateStateModel>({
  name: 'emailTemplates',
  defaults: {
    emailTemplates: [],
    loading: false,
    variables: []
  }
})
@Injectable()

export class EmailTemplateState {

  @Selector()
  static emailTemplates(state: EmailTemplateStateModel) {
    return state.emailTemplates.sort((a, b) => a.title.localeCompare(b.title));
  }

  @Selector()
  static userEmailTemplates(state: EmailTemplateStateModel) {
    return (userId: number) => state.emailTemplates.filter(t => t.lenderUserId === userId);
  }

  @Selector()
  static loading(state: EmailTemplateStateModel) {
    return state.loading;
  }

  @Selector()
  static variables(state: EmailTemplateStateModel) {
    return state.variables.filter(v => !v.hidden);
  }

  constructor(
    private messageService: MessageService,
    private store: Store
  ) { }

  @Action(GetEmailTemplates)
  getEmailTemplates(ctx: StateContext<EmailTemplateStateModel>, _: GetEmailTemplates) {
    if (ctx.getState().loading) {
      return;
    }
    ctx.patchState({ loading: true });
    return this.messageService.getTemplates().pipe(catchError(err => {
      ctx.patchState({ loading: false });
      this.store.dispatch(new CreateNewSettingsAlert(
        `There was a problem fetching your email templates. Please try again. Error message: ${err}`
      ));
      throw err;
    }), tap(emailTemplates => {
      ctx.patchState({
        emailTemplates,
        loading: false
      });
    }));
  }

  @Action(GetTemplateVariables)
  getTemplateVariables(ctx: StateContext<EmailTemplateStateModel>, _: GetTemplateVariables) {
    if (ctx.getState().variables.length) {
      return;
    }
    return this.messageService.getTemplateVariables().pipe(catchError(err => {
      this.store.dispatch(new CreateNewSettingsAlert(
        `There was a problem fetching template variables. Please refresh the page to try again. Error message: ${err}`
      ));
      throw err;
    }), tap(variables => {
      ctx.patchState({ variables });
    }));
  }

  @Action(CreateEmailTemplate)
  createEmailTemplate(ctx: StateContext<EmailTemplateStateModel>, { template }: CreateEmailTemplate) {
    ctx.patchState({ loading: true });
    return this.messageService.createTemplate(template).pipe(catchError(err => {
      ctx.patchState({ loading: false });
      this.store.dispatch(new CreateNewSettingsAlert(
        `There was a problem creating this email template. Please try again. Error message: ${err}`
      ));
      throw err;
    }), tap(newTemplate => {
      ctx.setState(
        patch({
          emailTemplates: append([ newTemplate ]),
          loading: false
        })
      );
    }));
  }

  @Action(UpdateEmailTemplate)
  updateEmailTemplate(ctx: StateContext<EmailTemplateStateModel>, { template }: UpdateEmailTemplate) {
    ctx.patchState({ loading: true });
    return this.messageService.updateTemplate(template).pipe(catchError(err => {
      ctx.patchState({ loading: false });
      this.store.dispatch(new CreateNewSettingsAlert(
        `There was a problem updating this email template. Please try again. Error message: ${err}`
      ));
      throw err;
    }), tap(updatedTemplate => {
      ctx.setState(
        patch({
          emailTemplates: updateItem<any>(t => t.id === updatedTemplate.id, updatedTemplate),
          loading: false
        })
      );
    }));
  }

  @Action(DeleteEmailTemplate)
  deleteEmailTemplate(ctx: StateContext<EmailTemplateStateModel>, { templateId }: DeleteEmailTemplate) {
    ctx.patchState({ loading: true });
    return this.messageService.deleteTemplate(templateId).pipe(catchError(err => {
      ctx.patchState({ loading: false });
      this.store.dispatch(new CreateNewSettingsAlert(
        `There was a problem deleting this email template. Please try again. Error message: ${err}`
      ));
      throw err;
    }), tap(() => {
      ctx.setState(
        patch({
          emailTemplates: removeItem<any>(t => t.id === templateId),
          loading: false
        })
      );
    }));
  }
}
